import { Link, useLocation, useParams } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import ChatPanel from '../components/ChatPanel'
import type { ChatThreadKind } from '../services/chatApi'

type ThreadKind = 'event' | 'pool' | 'outing'

type RouteState = {
  title?: string
  subtitle?: string
  locked?: boolean
  lockedMessage?: string
}

const configByKind: Record<
  ThreadKind,
  {
    backLabel: string
    backPath: (id: string) => string
    fallbackTitle: string
    fallbackSubtitle: string
    placeholder: string
  }
> = {
  event: {
    backLabel: 'Back to Event',
    backPath: (id) => `/events/${id}`,
    fallbackTitle: 'Event chat',
    fallbackSubtitle: 'RSVPs (yes) only',
    placeholder: 'Message this event…',
  },
  pool: {
    backLabel: 'Back to Pool',
    backPath: (id) => `/pools/${id}`,
    fallbackTitle: 'Pool chat',
    fallbackSubtitle: 'Pool participants only',
    placeholder: 'Message this pool…',
  },
  outing: {
    backLabel: 'Back to Outing',
    backPath: (id) => `/golf/outings/${id}`,
    fallbackTitle: 'Outing chat',
    fallbackSubtitle: 'Outing participants only',
    placeholder: 'Message this outing…',
  },
}

export default function ChatThreadPage() {
  const { kind, entityId } = useParams<{ kind: ThreadKind; entityId: string }>()
  const location = useLocation()
  const state = (location.state || {}) as RouteState

  if (!kind || !entityId || !configByKind[kind]) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Chat not found</h1>
        </div>
      </div>
    )
  }

  const config = configByKind[kind]
  const title = state.title || config.fallbackTitle
  const subtitle = state.subtitle || config.fallbackSubtitle

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 px-4 sm:px-6 py-3">
        <Link
          to={config.backPath(entityId)}
          className="inline-flex items-center text-sm font-medium text-white/80 hover:text-white"
          state={location.state}
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          {config.backLabel}
        </Link>
      </div>

      <div className="flex-1 min-h-0 px-0 sm:px-4 sm:pb-4">
        <ChatPanel
          kind={kind as ChatThreadKind}
          entityId={entityId}
          title={title}
          subtitle={subtitle}
          placeholder={config.placeholder}
          locked={!!state.locked}
          lockedMessage={state.lockedMessage}
          embedded
          className="h-full sm:rounded-xl sm:border sm:border-gray-200 sm:dark:border-gray-700 sm:overflow-hidden"
        />
      </div>
    </div>
  )
}
